import type { Capture } from './types';

type Text = (key: string, values?: Record<string, string | number>) => string;

/** Where a snapshot's sources were taken from, as a person reads it */
export type Source = 'main' | 'conversation' | 'local' | 'external' | 'conduict';

/** Who sends a pending snapshot's packages */
export type Sender = 'local' | 'external' | 'conduict' | 'workspace' | 'packages';

/**
 * What a person reads about a snapshot that the service does not say in words: its title (or one made from
 * its packages and its time), its size, where its sources came from and who sends its packages.
 */
export class Describe {
	/** The size of its packages' archives, in bytes */
	static size(capture: Capture): number {
		return capture.packages.reduce((total, entry) => total + (entry.bytes ?? 0), 0);
	}

	/** Its title, or one made from its packages and when it was made: "@acme/ui and 2 more · Oct 17, 15:53" */
	static title(capture: Capture, text: Text, date: (value: string) => string): string {
		if (capture.title) return capture.title;
		const [first] = capture.packages;
		const more = capture.packages.length - 1;
		const named = !first ? text('captures.untitled') : more > 0 ? text('title.more', { name: first.name, count: more }) : first.name;
		return text('title.made', { packages: named, time: date(capture.created) });
	}

	/** The copy it came from when one was said, else how the development service was reached */
	static source(capture: Capture): Source | undefined {
		return capture.provenance?.copy ?? capture.provenance?.connection;
	}

	/** Who sends its packages: the producer that started it, or Packages and how it was reached */
	static sender(capture: Capture): Sender {
		if (capture.producer === 'conduict' || capture.provenance?.connection === 'conduict') return 'conduict';
		if (capture.producer === 'workspace') return 'workspace';
		const connection = capture.provenance?.connection;
		return connection === 'local' || connection === 'external' ? connection : 'packages';
	}
}
